<template>
  <div>
    <v-list-item
      :id="commentId"
      :class="[
        color,
        'comment-list-item',
        { 'highlight-element': isHighlighted },
      ]"
    >
      <template #prepend>
        <template v-if="user">
          <UserAvatar :user="user" />
        </template>
        <template v-else>
          <v-avatar>
            <template #default>
              <v-icon>mdi-account</v-icon>
            </template>
          </v-avatar>
        </template>
      </template>
      <template #default>
        <div class="comment-wrapper">
          <div class="comment-header">
            <strong
              >{{ user?.first_name || "Unknown" }}
              {{ user?.last_name || "" }} schreef:</strong
            >
          </div>
          <div class="comment-content">
            <div class="comment-text" v-html="sanitizedContent"></div>
            <div class="text-caption time-ago">
              {{ timeAgo }}
            </div>
          </div>
        </div>
      </template>
      <template #append>
        <CommentOptions
          :comment="comment"
          @click.stop.prevent
          @edit_comment="$emit('edit_comment', $event)"
          @comment_deleted="$emit('comment_deleted', $event)"
        />
      </template>
    </v-list-item>
    <v-divider></v-divider>
  </div>
</template>

<script>
import { computed, onMounted } from "vue";
import moment from "moment";
import UserAvatar from "../users/UserAvatar.vue";
import CommentOptions from "./CommentOptions.vue";
import { useUsersStoreRefactor } from "@/stores/usersStoreRefactor";
import { useRoute } from "vue-router";
import { sanitizeHtml } from "@/utils/sanitizeHtml";

export default {
  name: "CommentItem",
  components: {
    UserAvatar,
    CommentOptions,
  },
  props: ["comment", "color"],
  setup(props) {
    const usersStore = useUsersStoreRefactor();
    const route = useRoute();

    const timeAgo = computed(() => {
      return moment(props.comment.created_at).fromNow();
    });

    const user = computed(() => {
      const userId = props.comment.user_id;
      console.log("Looking up user with ID:", userId);
      const foundUser = usersStore.getUserById(userId);
      console.log("Found user:", foundUser);
      return foundUser;
    });

    const sanitizedContent = computed(() => {
      return sanitizeHtml(props.comment.content);
    });

    // Generate the comment ID for targeting
    const commentId = computed(() => {
      return `c${props.comment.id}`;
    });

    // Check if this comment is the highlighted one from URL
    const isHighlighted = computed(() => {
      return (
        route.query.comment &&
        Number(route.query.comment) === Number(props.comment.id)
      );
    });

    // Debug mounted
    onMounted(() => {
      console.log(`CommentItem: Mounted comment with ID ${commentId.value}`);

      // Check if this comment matches a query parameter in the URL
      if (isHighlighted.value) {
        console.log(
          `CommentItem: This comment matches the URL query parameter comment=${route.query.comment}`,
        );
      }
    });

    return {
      timeAgo,
      user,
      sanitizedContent,
      commentId,
      isHighlighted,
    };
  },
};
</script>

<style scoped>
.comment-list-item {
  padding: 12px;
}

.comment-wrapper {
  width: 100%;
  display: flex;
  flex-direction: column;
}

.comment-header {
  font-size: 1rem;
  margin-bottom: 4px;
  font-weight: 500;
}

.comment-content {
  white-space: normal !important;
  overflow: visible !important;
  text-overflow: clip !important;
  max-width: 100%;
  margin-top: 4px;
}

.comment-text {
  line-height: 1.5;
  word-break: break-word;
  white-space: pre-wrap;
  overflow-wrap: break-word;
  width: 100%;
}

.time-ago {
  margin-top: 8px;
  color: rgba(0, 0, 0, 0.6);
}

/* Highlight styles */
.highlight-element {
  background-color: rgba(255, 193, 7, 0.1);
  transition: background-color 0.3s ease;
}

.comment-text :deep(p) {
  margin-bottom: 8px;
}

.comment-text :deep(blockquote) {
  border-left: 3px solid rgba(0, 0, 0, 0.12);
  padding-left: 12px;
  margin: 8px 0;
  color: rgba(0, 0, 0, 0.6);
}

.comment-text :deep(ul),
.comment-text :deep(ol) {
  padding-left: 24px;
  margin: 8px 0;
}

.comment-text :deep(img) {
  max-width: 100%;
  height: auto;
}

@media (max-width: 600px) {
  .comment-list-item {
    padding: 8px;
  }
}
</style>
